import type { ReactNode } from 'react'
import { Alert, Box, CircularProgress, Divider, Stack, Typography } from '@mui/material'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner'
import type { LeituraNaTela } from './use-leitor-resumo'

/** Mesmo quadro de 72px da linha de item do resumo, para a leitura ocupar o lugar em que o item vai aparecer. */
function Quadro({ children }: { children: ReactNode }): JSX.Element {
  return (
    <Box
      sx={{
        width: 72,
        height: 72,
        flexShrink: 0,
        display: 'flex',
        borderRadius: 2,
        border: '1px solid',
        alignItems: 'center',
        borderColor: 'divider',
        justifyContent: 'center',
        backgroundColor: 'action.hover',
      }}
    >
      {children}
    </Box>
  )
}

function LinhaLeitura({ leitura }: { leitura: LeituraNaTela }): JSX.Element {
  if (leitura.estado === 'RECUSADA') {
    return (
      <Alert severity="warning" sx={{ my: 1.5, borderRadius: 2, alignItems: 'center', '& .MuiAlert-message': { fontSize: '1.05rem' } }}>
        {leitura.texto}
      </Alert>
    )
  }

  return (
    <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 1.5 }}>
      <Quadro>
        <CircularProgress size={28} />
      </Quadro>

      <Stack spacing={0.25} sx={{ minWidth: 0 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
          Adicionando {leitura.nome}…
        </Typography>

        <Typography variant="body1" color="text.secondary">
          Lido no leitor de código de barras
        </Typography>
      </Stack>
    </Stack>
  )
}

interface LinhasDoLeitorProps {
  leituras: LeituraNaTela[]
  dica: boolean
  /** O leitor espera um instante entre um produto e outro: a dica pede para aguardar. */
  pausado?: boolean
}

/**
 * O que o leitor está fazendo, no fim da lista do resumo — onde o item vai aparecer. Com o leitor ligado, a
 * lista termina com a dica de que dá para passar o produto.
 */
function LinhasDoLeitor({ leituras, dica, pausado = false }: LinhasDoLeitorProps): JSX.Element | null {
  if (leituras.length === 0 && !dica) return null

  return (
    <>
      {leituras.map((leitura) => (
        <Box key={leitura.id}>
          <Divider sx={{ borderStyle: 'dashed' }} />
          <LinhaLeitura leitura={leitura} />
        </Box>
      ))}

      {dica && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ mt: 1.5, p: 2, borderRadius: 2, border: '1.5px dashed', borderColor: 'divider', color: 'text.secondary' }}
        >
          {pausado ? <HourglassEmptyIcon sx={{ fontSize: 32 }} /> : <QrCodeScannerIcon sx={{ fontSize: 32 }} />}

          <Typography variant="body1">
            {pausado
              ? 'Aguarde um instante para passar o próximo produto.'
              : 'Passe o código de barras do produto no leitor para adicionar à conta.'}
          </Typography>
        </Stack>
      )}
    </>
  )
}

export default LinhasDoLeitor
